import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { MessageSquare, Send, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { ChatMessage, ChatWidgetProps } from '../types';
import { formatRelativeTime } from '../utils/formatRelativeTime';

// Create a schema for chat creation
const createChatSchema = z.object({
  message: z.string(),
  conversation_id: z.string().optional(),
  organisation_id: z.string(),
});

const q = new QueryClient();
export const ChatWidget = ({ organisationId, baseUrl = 'https://lumi-web.pages.dev', initialExpanded = false }: ChatWidgetProps) => {
  return <ChatWidget2 organisationId={organisationId} baseUrl={baseUrl} initialExpanded={initialExpanded} />;
};

const ChatWidget2 = ({ organisationId, initialExpanded, baseUrl }: ChatWidgetProps) => {
  const [input, setInput] = useState('');
  const [feedback, setFeedback] = useState<Record<string, boolean>>({});
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const { mutateAsync: submitChat, isPending: isSubmittingChat } = useMutation(
    {
      mutationKey: ['submitChat'],
      mutationFn: async ({ message, conversation_id, organisation_id }: z.infer<typeof createChatSchema>) => {
        const resp = await fetch(`${baseUrl}/api/chats`, {
          method: 'POST',
          body: JSON.stringify({ message, conversation_id, organisation_id }),
        });

        if (!resp.ok) {
          throw new Error('Failed to submit chat');
        }

        return (await resp.json()) as ChatMessage;
      },
    },
    q
  );
  const { data: messages, refetch: refetchChats } = useQuery(
    {
      queryKey: ['conversationChats', conversationId],
      queryFn: async ({ queryKey: [, conversationId] }) => {
        if (!conversationId) {
          return [];
        }

        const resp = await fetch(`${baseUrl}/api/chats/${conversationId}`);

        if (!resp.ok) {
          throw new Error('Failed to fetch chats');
        }

        const data = (await resp.json()) as { data: ChatMessage[] };

        return data.data;
      },
      enabled: isExpanded && !!conversationId,
      refetchInterval: 3000,
    },
    q
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isExpanded) {
      scrollToBottom();
    }
  }, [messages, isExpanded]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const data = await submitChat({
      message: input,
      conversation_id: conversationId ?? undefined,
      organisation_id: organisationId,
    });

    setConversationId(data.conversation_id);
    await refetchChats();
    setInput('');
  };

  const handleFeedback = async (messageId: string, isPositive: boolean) => {
    console.log('handleFeedback', messageId, isPositive);
  };

  const renderMessageContent = (text: string) => {
    const parts = text.split(/(!?\[.*?\]\(.*?\))/);
    return parts.map((part, index) => {
      const imageMatch = part.match(/!\[(.*?)\]\((.*?)\)/);
      if (imageMatch) {
        const [, alt, src] = imageMatch;
        return <img key={index} src={src} alt={alt} className="rounded-lg w-full h-auto my-2 object-cover" />;
      }
      return (
        <p key={index} className="text-[15px] leading-relaxed whitespace-pre-wrap">
          {part}
        </p>
      );
    });
  };

  const handleStartNewChat = async () => {
    setConversationId(null);
    await refetchChats();
    setFeedback({});
    setIsExpanded(true);
  };

  const toggleMode = () => {
    handleStartNewChat();
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {!isExpanded ? (
        <button
          onClick={() => setIsExpanded(true)}
          className="group w-14 h-14 rounded-full bg-lumi-500 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center hover:bg-lumi-600"
        >
          <MessageSquare className="w-6 h-6 transition-transform group-hover:scale-110" />
        </button>
      ) : (
        <div className="w-[380px] h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-slideUp">
          <div className="bg-lumi-500 text-white px-4 py-2.5 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1.5 mt-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                  <p className="text-xs text-white/80">Ready to assist you</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={handleStartNewChat} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" title="Start New Chat">
                  <MessageSquare size={16} />
                </button>
                <button onClick={() => setIsExpanded(false)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages?.map((message) => (
              <div key={message.id} className={`flex ${message.sender_type === 'user' ? 'justify-end' : 'justify-start'} items-end space-x-2`}>
                {message.sender_type === 'ai' && (
                  <div className="w-7 h-7 rounded-full bg-lumi-500 flex items-center justify-center text-white text-xs font-medium shrink-0">AI</div>
                )}
                <div className="flex flex-col gap-1 max-w-[85%]">
                  <div className={`px-4 py-2 rounded-2xl ${message.sender_type === 'user' ? 'bg-lumi-500 text-white' : 'bg-white shadow-sm border border-gray-100'}`}>
                    {renderMessageContent(message.payload)}
                  </div>
                  {message.sender_type === 'admin' && (
                    <div className="flex gap-0.5 self-start">
                      <button
                        onClick={() => handleFeedback(message.id, true)}
                        className={`p-1 rounded-full transition-colors ${feedback[message.id] === true ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'}`}
                        title="Helpful response"
                      >
                        <ThumbsUp size={12} />
                      </button>
                      <button
                        onClick={() => handleFeedback(message.id, false)}
                        className={`p-1 rounded-full transition-colors ${feedback[message.id] === false ? 'text-red-600' : 'text-gray-400 hover:text-gray-600'}`}
                        title="Unhelpful response"
                      >
                        <ThumbsDown size={12} />
                      </button>
                    </div>
                  )}
                  <div className="text-xs text-gray-400">{formatRelativeTime(new Date(message.created_at))}</div>
                </div>
                {message.sender_type === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-medium shrink-0">You</div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="flex flex-col bg-white border-t border-gray-100">
            <div className="p-4">
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lumi-500 text-sm"
                />
                <button
                  type="submit"
                  disabled={!input.trim()}
                  className="p-2.5 bg-lumi-500 text-white rounded-full hover:bg-lumi-600 focus:outline-none focus:ring-2 focus:ring-lumi-500 disabled:opacity-50 transition-colors"
                >
                  <Send size={18} />
                </button>
              </form>
            </div>
            <div className="px-4 pb-4 text-center">
              <p className="text-xs text-gray-400">
                Powered by{' '}
                <a href="https://www.thelumi.ai" target="_blank" rel="noopener noreferrer" className="text-lumi-500 hover:text-lumi-600">
                  Lumi by Opaper
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
